import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Layout } from '@/components/layout'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useDocumentStore } from '@/lib/stores/documentStore'
import { useIndexedFoldersStore } from '@/lib/stores/indexedFoldersStore'
import { FileBrowser } from '@/features/documents/components/FileBrowser'
import { IndexingStatusGrid } from '@/features/documents/IndexingStatusGrid'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { BookOpen, RefreshCw, AlertCircle, Clock, Settings } from 'lucide-react'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function PlanvormingPage() {
    const { t } = useTranslation()
    const { activeCrisis } = useCrisisStore()
    const { getIndexStatus, triggerIndexing, error, clearCache, fetchAllDocuments, indexStatus } =
        useDocumentStore()
    const {
        fetchFolders,
        startIndexing,
        pollAllFolderStatuses,
        stopPollingStatuses,
        folders,
        folderStatuses,
        error: indexingError,
    } = useIndexedFoldersStore()
    const [isReindexing, setIsReindexing] = useState(false)
    const [reindexSuccess, setReindexSuccess] = useState(false)
    const statusCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const canRefetchDocuments = useHasPermission('documents.refetch')
    const canManageDocuments = useHasPermission('documents.manage')
    const canManageFolders = useHasPermission('indexed-folders.manage')
    const activeFolderIds = useMemo(
        () => (folders ?? []).filter(folder => folder?.isActive).map(folder => folder.id),
        [folders]
    )
    const isAnyFolderIndexing = useMemo(
        () => Object.values(folderStatuses ?? {}).some(status => status?.status === 'indexing'),
        [folderStatuses]
    )
    const combinedError = error ?? indexingError

    // Fetch all documents and folders on crisis activation
    useEffect(() => {
        if (activeCrisis) {
            fetchAllDocuments(String(activeCrisis.id))
            getIndexStatus(String(activeCrisis.id))
            fetchFolders()
        }
    }, [activeCrisis, fetchAllDocuments, getIndexStatus, fetchFolders])

    // Poll indexing status while indexing is in progress
    useEffect(() => {
        const shouldPollDocumentIndex = Boolean(activeCrisis) && !indexStatus?.hasIndex

        if (!shouldPollDocumentIndex) {
            if (statusCheckIntervalRef.current) {
                clearInterval(statusCheckIntervalRef.current)
                statusCheckIntervalRef.current = null
            }
            return
        }

        if (!statusCheckIntervalRef.current && activeCrisis) {
            statusCheckIntervalRef.current = setInterval(() => {
                getIndexStatus(String(activeCrisis.id))
            }, 5000) // Check every 5 seconds
        }

        return () => {
            if (statusCheckIntervalRef.current) {
                clearInterval(statusCheckIntervalRef.current)
                statusCheckIntervalRef.current = null
            }
        }
    }, [activeCrisis, indexStatus?.hasIndex, getIndexStatus])

    useEffect(() => {
        if (!activeCrisis) {
            stopPollingStatuses()
            return
        }

        if (isAnyFolderIndexing) {
            void pollAllFolderStatuses()
            return
        }

        stopPollingStatuses()
    }, [activeCrisis, isAnyFolderIndexing, pollAllFolderStatuses, stopPollingStatuses])

    useEffect(() => {
        return () => {
            if (statusCheckIntervalRef.current) {
                clearInterval(statusCheckIntervalRef.current)
                statusCheckIntervalRef.current = null
            }
            stopPollingStatuses()
        }
    }, [stopPollingStatuses])

    const handleReindex = async () => {
        if (!activeCrisis || !canRefetchDocuments) return
        setIsReindexing(true)
        setReindexSuccess(false)
        try {
            // Trigger indexing via indexed folders store if available
            await startIndexing(activeFolderIds.length > 0 ? activeFolderIds : undefined)
            clearCache()
            setReindexSuccess(true)
            // Start polling for real-time updates
            await pollAllFolderStatuses()
            setTimeout(() => setReindexSuccess(false), 5000)
        } catch {
            // error is set in the store
            // Fallback to old indexing method if new method fails
            try {
                await triggerIndexing(String(activeCrisis.id))
                clearCache()
                setReindexSuccess(true)
                setTimeout(() => setReindexSuccess(false), 5000)
            } catch {
                // error is set in the store
            }
        } finally {
            setIsReindexing(false)
        }
    }

    return (
        <Layout>
            <div className="flex flex-col gap-6 p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg shrink-0">
                            <BookOpen className="w-6 h-6 text-orange-600" aria-hidden="true" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">{t('planvorming.titel')}</h1>
                            <p className="text-muted-foreground text-sm mt-0.5">{t('planvorming.subtitel')}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                        {canManageFolders && (
                            <Button variant="outline" asChild className="flex items-center gap-2 min-h-[44px]">
                                <Link to="/admin/indexed-folders">
                                    <Settings className="w-4 h-4" aria-hidden="true" />
                                    {t('planvorming.manageFolders')}
                                </Link>
                            </Button>
                        )}
                        {canRefetchDocuments && (
                            <Button
                                variant="outline"
                                onClick={handleReindex}
                                disabled={isReindexing || !activeCrisis}
                                className="flex items-center gap-2 min-h-[44px]"
                            >
                                <RefreshCw className={`w-4 h-4 ${isReindexing ? 'animate-spin' : ''}`} aria-hidden="true" />
                                {isReindexing ? t('planvorming.reindexing') : t('planvorming.reindexAll')}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Indexed Folders Status Grid */}
                {canManageDocuments && (
                    <div className="space-y-2">
                        <h2 className="text-lg font-semibold">{t('planvorming.indexingStatus')}</h2>
                        <IndexingStatusGrid />
                    </div>
                )}


                {/* Indexing in progress alert */}
                {canManageDocuments && ((indexStatus && !indexStatus.hasIndex) || isAnyFolderIndexing) ? (
                    <Alert className="border-blue-200 bg-blue-50 flex items-start gap-3">
                        <Clock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <AlertDescription className="text-blue-900 font-medium">
                                {t('planvorming.indexingInProgress')}
                            </AlertDescription>
                            <p className="text-sm text-blue-800 mt-1">
                                {t('planvorming.indexingCheckBack')}
                            </p>
                        </div>
                    </Alert>
                ) : null}

                {/* Success/error alerts */}
                {reindexSuccess && (
                    <Alert className="border-green-200 bg-green-50">
                        <AlertDescription className="text-green-800">
                            {t('planvorming.indexingStartedDescription')}
                        </AlertDescription>
                    </Alert>
                )}
                {combinedError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{combinedError}</AlertDescription>
                    </Alert>
                )}

                {/* File explorer with integrated search (disabled while indexing) */}
                <Card
                    className={
                        ((indexStatus && !indexStatus.hasIndex) || isAnyFolderIndexing)
                            ? 'opacity-50 pointer-events-none'
                            : ''
                    }
                >
                    <CardContent className="pt-6">
                        <FileBrowser />
                    </CardContent>
                </Card>
            </div>
        </Layout>
    )
}
